import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { smsParts } from '../../../../utils/sms';
import {
  ConnectedPosition,
  ConnectedOverlayPositionChange,
  Overlay,
  OverlayModule,
} from '@angular/cdk/overlay';

export interface BkSelectOption {
  value: string;
  label: string;
  /** Short right-aligned note, e.g. "Short code". */
  hint?: string;
  /** Second line under the label, e.g. a template's text. */
  detail?: string;
}

type RawOption = unknown;

/** An option as listed: its label split around the search match. */
interface ShownOption extends BkSelectOption {
  pre: string;
  hit: string;
  post: string;
}

let nextId = 0;

/** Lists longer than this get a search field. */
const SEARCH_FROM = 7;

/**
 * Searchable single select in the console style: a field-shaped trigger and a
 * listbox in a CDK overlay (so panels with overflow never clip it). Works with
 * formControlName / ngModel.
 *
 *   <input-field label="Sender ID">
 *     <bk-select formControlName="senderId" [options]="senderIDs" valueKey="senderId"
 *                variant="sender" placeholder="Choose a sender ID" />
 *   </input-field>
 *
 * Keyboard: Enter, Space or arrows open it; typing opens it with the search
 * filled in. Inside, arrows, Home/End and PageUp/PageDown move, Enter picks,
 * Escape closes, and Tab closes and moves on.
 */
@Component({
  selector: 'bk-select',
  standalone: true,
  imports: [OverlayModule],
  templateUrl: './bk-select.component.html',
  styleUrl: './bk-select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BkSelectComponent), multi: true },
  ],
})
export class BkSelectComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  readonly scrollStrategy = inject(Overlay).scrollStrategies.reposition();

  /** Strings, `{ value, label, hint }`, or any objects read through valueKey/labelKey. */
  @Input() set options(list: readonly RawOption[] | null | undefined) {
    this.raw = list ?? [];
    this.normalise();
  }
  @Input() set valueKey(key: string) {
    this.vKey = key;
    this.normalise();
  }
  @Input() set labelKey(key: string) {
    this.lKey = key;
    this.normalise();
  }
  /** Read a second line from each object (searched too). */
  @Input() set detailKey(key: string) {
    this.dKey = key;
    this.normalise();
  }
  @Input() placeholder = 'Choose an option';
  /** Shown in the trigger when there is nothing to choose from. */
  @Input() emptyText = 'Nothing to choose from';
  /** Singular and plural noun for the search placeholder and counts. */
  @Input() noun = 'option';
  @Input() nounPlural = '';
  @Input() loading = false;
  /**
   * 'sender' adds an initials tile and marks numeric sender IDs as short codes.
   * 'template' shows each template's text under its name and how many SMS it takes.
   */
  @Input() set variant(kind: 'plain' | 'sender' | 'template') {
    this.kind = kind;
    this.normalise();
  }
  get variant(): 'plain' | 'sender' | 'template' {
    return this.kind;
  }
  /** id for the trigger, so a plain <label for> can name it. */
  @Input() inputId: string | null = null;
  @Input() describedBy: string | null = null;
  /** Without a form control: [value] in, (valueChange) out. */
  @Input('value') set valueInput(value: string | null | undefined) {
    this.writeValue(value);
  }
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('trigger', { static: true }) private trigger!: ElementRef<HTMLButtonElement>;
  @ViewChild('search') private searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('list') private list?: ElementRef<HTMLElement>;

  readonly id = `bks-${nextId++}`;
  readonly listId = `${this.id}-list`;
  readonly panelId = `${this.id}-panel`;
  /** A modal around the field: it adopts the panel while open (aria-owns). */
  private owner: HTMLElement | null = null;

  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  all: BkSelectOption[] = [];
  shown: ShownOption[] = [];
  value = '';
  query = '';
  open = false;
  above = false;
  disabled = false;
  active = -1;
  panelWidth = 280;

  private raw: readonly RawOption[] = [];
  private vKey = '';
  private lKey = '';
  private dKey = '';
  private kind: 'plain' | 'sender' | 'template' = 'plain';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  get selected(): BkSelectOption | undefined {
    return this.all.find((option) => option.value === this.value);
  }

  get searchable(): boolean {
    return this.all.length > SEARCH_FROM;
  }

  get plural(): string {
    return this.nounPlural || `${this.noun}s`;
  }

  get activeId(): string | null {
    return this.active >= 0 && this.shown[this.active] ? this.optionId(this.active) : null;
  }

  optionId(index: number): string {
    return `${this.id}-opt-${index}`;
  }

  // ── ControlValueAccessor ──
  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.close(false);
    this.cdr.markForCheck();
  }

  // ── Opening and closing ──
  toggle() {
    this.open ? this.close(true) : this.openPanel();
  }

  openPanel(initialQuery = '') {
    if (this.disabled || this.open) return;
    this.panelWidth = Math.max(240, Math.round(this.trigger.nativeElement.getBoundingClientRect().width));
    this.query = initialQuery;
    this.filter();
    const current = this.shown.findIndex((option) => option.value === this.value);
    this.active = initialQuery ? (this.shown.length ? 0 : -1) : Math.max(current, 0);
    this.open = true;
    this.cdr.markForCheck();
  }

  /** Runs once the overlay is attached: focus goes into it. */
  onAttached() {
    this.adopt(true);
    requestAnimationFrame(() => {
      if (!this.open) return;
      if (this.searchInput) {
        const input = this.searchInput.nativeElement;
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      } else {
        this.list?.nativeElement.focus();
      }
      this.scrollActiveIntoView('center');
    });
  }

  close(returnFocus: boolean) {
    if (!this.open) return;
    this.open = false;
    this.adopt(false);
    this.onTouched();
    this.cdr.markForCheck();
    if (returnFocus) this.trigger.nativeElement.focus();
  }

  onOutsideClick(event: MouseEvent) {
    // The trigger toggles on its own click; don't close here and reopen there.
    if (this.trigger.nativeElement.contains(event.target as Node)) return;
    this.close(false);
  }

  onPositionChange(change: ConnectedOverlayPositionChange) {
    this.above = change.connectionPair.originY === 'top';
    this.cdr.detectChanges();
  }

  // ── Choosing ──
  choose(option: BkSelectOption) {
    if (option.value !== this.value) {
      this.value = option.value;
      this.onChange(option.value);
      this.valueChange.emit(option.value);
    }
    this.close(true);
  }

  onSearch(text: string) {
    this.query = text;
    this.filter();
    this.active = this.shown.length ? 0 : -1;
    this.scrollActiveIntoView();
  }

  // ── Keyboard ──
  onTriggerKeydown(event: KeyboardEvent) {
    if (this.open) return;
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.openPanel();
      return;
    }
    // Typing on the closed field starts a search, like a native select.
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      if (this.searchable) {
        this.openPanel(event.key);
      } else {
        const index = this.all.findIndex((o) => o.label.toLowerCase().startsWith(event.key.toLowerCase()));
        if (index >= 0) this.choose(this.all[index]);
      }
    }
  }

  onPanelKeydown(event: KeyboardEvent) {
    const inSearch = event.target === this.searchInput?.nativeElement;
    const last = this.shown.length - 1;
    const move = (to: number) => {
      event.preventDefault();
      if (last < 0) return;
      this.active = Math.min(Math.max(to, 0), last);
      this.scrollActiveIntoView();
    };

    switch (event.key) {
      case 'ArrowDown':
        return move(this.active + 1);
      case 'ArrowUp':
        return move(this.active - 1);
      case 'PageDown':
        return move(this.active + 8);
      case 'PageUp':
        return move(this.active - 8);
      case 'Home':
        if (!inSearch) move(0);
        return;
      case 'End':
        if (!inSearch) move(last);
        return;
      case 'Enter':
        event.preventDefault();
        if (this.shown[this.active]) this.choose(this.shown[this.active]);
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation(); // a modal around the field stays open
        this.close(true);
        return;
      case 'Tab':
        // Focus the trigger first; the browser's Tab then moves on from it.
        this.close(true);
        return;
    }
  }

  initials(label: string): string {
    const clean = label.replace(/[^\p{L}\p{N}\s_-]/gu, '').trim();
    const words = clean.split(/[\s_-]+/).filter(Boolean);
    if (!words.length) return label.slice(0, 2).toUpperCase() || '·';
    if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
    return words[0].slice(0, 2).toUpperCase();
  }

  // ── Internals ──

  /**
   * The panel lives on <body>; inside an aria-modal dialog screen readers
   * would treat it as outside the dialog, so the dialog owns it while open.
   */
  private adopt(on: boolean) {
    if (on) this.owner = this.trigger.nativeElement.closest<HTMLElement>('[aria-modal="true"]');
    const owner = this.owner;
    if (!owner) return;
    const ids = (owner.getAttribute('aria-owns') ?? '').split(/\s+/).filter((id) => id && id !== this.panelId);
    if (on) ids.push(this.panelId);
    if (ids.length) owner.setAttribute('aria-owns', ids.join(' '));
    else owner.removeAttribute('aria-owns');
    if (!on) this.owner = null;
  }
  private normalise() {
    const seen = new Set<string>();
    const list: BkSelectOption[] = [];
    for (const item of this.raw) {
      const option = this.toOption(item);
      if (!option || seen.has(option.value)) continue; // the API can repeat a sender ID
      seen.add(option.value);
      list.push(option);
    }
    this.all = list;
    this.filter();
    this.cdr.markForCheck();
  }

  private toOption(item: RawOption): BkSelectOption | null {
    let value: string;
    let label: string;
    let hint: string | undefined;
    let detail: string | undefined;
    if (typeof item === 'string') {
      value = label = item;
    } else if (item && typeof item === 'object' && this.vKey && this.vKey in item) {
      const record = item as Record<string, unknown>;
      value = String(record[this.vKey] ?? '');
      label = String(record[this.lKey || this.vKey] ?? value);
      if (this.dKey && record[this.dKey] != null) detail = String(record[this.dKey]);
    } else if (item && typeof item === 'object' && 'value' in item) {
      const option = item as BkSelectOption;
      value = String(option.value ?? '');
      label = String(option.label ?? value);
      hint = option.hint;
      detail = option.detail;
    } else {
      return null;
    }
    if (!value) return null;
    if (!hint && this.kind === 'sender' && /^[*#+]?\d+$/.test(value)) hint = 'Short code';
    if (!hint && this.kind === 'template' && detail) {
      const sms = smsParts(detail);
      if (sms.parts) hint = `${sms.estimated ? 'About ' : ''}${sms.parts} SMS`;
    }
    // One line of the text is enough to recognise it.
    if (detail) detail = detail.replace(/\s+/g, ' ').trim() || undefined;
    return { value, label, hint, detail };
  }

  private filter() {
    const q = this.query.trim().toLowerCase();
    this.shown = [];
    for (const option of this.all) {
      const at = q ? option.label.toLowerCase().indexOf(q) : -1;
      // A template can be found by its text as well as its name.
      if (q && at < 0 && !option.detail?.toLowerCase().includes(q)) continue;
      this.shown.push(
        at < 0
          ? { ...option, pre: option.label, hit: '', post: '' }
          : {
              ...option,
              pre: option.label.slice(0, at),
              hit: option.label.slice(at, at + q.length),
              post: option.label.slice(at + q.length),
            }
      );
    }
  }

  private scrollActiveIntoView(block: ScrollLogicalPosition = 'nearest') {
    const id = this.activeId;
    if (!id) return;
    this.cdr.detectChanges();
    document.getElementById(id)?.scrollIntoView({ block });
  }
}
